import { 
  ClientErrorCode, FetchReturn, FetchReturnError, GlobalMessage, SuccessCode, 
  SuccessNoResponseCode, actions
} from '../store';
import store from '../store/store';
import { sendMessage } from '../store/globalMessages/actions';
import { bindActionCreators } from '@reduxjs/toolkit';
import { FORCE_LOGOUT_REASON, PAGE_URL } from '../constants';
import { cloneDeep } from 'lodash-es';
import { AMPLIFY_ERROR_CODE } from './cognitoErrorUtils';
import { ActionType } from '../store/actionTypes';
import { cabCaptureException, cabCaptureMessage } from './logging';
import { fetchAuthSession as amplifyFetchAuthSession } from 'aws-amplify/auth';
import { logout } from '../store/auth/actions';

interface FetchContent {
  headers?: { [key: string]: string };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body?: any;
  method?: string;
}

export const makeAuthHeader = (token?: string | null): string => {
  return `bearer ${token}`;
};

export const makeHeaders = async (
  shouldAuthenticate?: boolean, allowUnauthenticated?: boolean
): Promise<FetchContent['headers']> => {
  const actionCreators = bindActionCreators({
    logout: actions.auth.logout
  }, store.dispatch);

  const headers: { [key: string]: string } = {
    'Content-Type': 'application/json',
    'Pragma': 'no-cache',
    'FrontendVersion': import.meta.env.VITE_APP_FRONTEND_VERSION || '',
    'AppName': "app"
  };

  if (shouldAuthenticate) {
    try {
      const session = await amplifyFetchAuthSession();
      const token = session.tokens?.idToken?.toString() || null;
      if (token) {
        headers['Authorization'] = makeAuthHeader(token);
      } else if (!allowUnauthenticated) {
        actionCreators.logout();
      }
    } catch (err) {
      // err can sometimes be a string or an Error object. Difficult to determine which until here.
      if (err instanceof Error && "code" in err && err['code'] !== AMPLIFY_ERROR_CODE.NETWORK_ERROR) {
        actionCreators.logout();
      }
    }
  }

  return headers;
};

interface Params {
  [key: string]: string | string[] | number | number[] | boolean | boolean[] | undefined;
}

export const makeParams = (params: Params): string => {
  const params_array: string[] = [];
  Object.keys(params).forEach((key: string): void => {
    let value = params[key];
    if (typeof value === 'boolean') {
      value = value ? 1 : 0;
    }
    if (value instanceof Array) {
      value.forEach((value2: string | number | boolean): void => {
        const sanitizedValue2 = typeof value2 === 'boolean' ? Number(value2) : value2;
        params_array.push(`${key}=${encodeURIComponent(sanitizedValue2)}`);
      });
    } else if (value !== undefined) {
      params_array.push(`${key}=${encodeURIComponent(value)}`);
    }
  });

  return "?" + params_array.join("&");
};

//Background is true for fetch requests that shouldn't show errors if the backend is unavailable at the moment
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchData = async <T = any, E extends FetchReturnError = string>(
  url: string, content: FetchContent, background?: boolean
): Promise<FetchReturn<T, E>> => {
  return fetch(url, content)
    .then(async (res): Promise<FetchReturn<T, E>> => {
      if (url.length > 2048) {
        cabCaptureMessage(`Fetched URL longer than 2048 characters: ${url}`);
      }

      const status = res.status as SuccessCode | ClientErrorCode | SuccessNoResponseCode;
      if (status === 410 && window.location.pathname !== PAGE_URL.UPDATE) {
        //API VERSION IS OUT OF DATE
        return res.json().then((data) => {
          if (data.error_code === 'invalid_cabinet_version') {
            const redirectPathname = window.location.pathname;
            window.history.replaceState({referrer: redirectPathname}, '', PAGE_URL.UPDATE);
            window.location.pathname = PAGE_URL.UPDATE;
            cabCaptureMessage('Incorrect Version: ' + url);
          }
          return { status: status, data };
        });
      } else if (status === 503 && window.location.pathname !== PAGE_URL.MAINTENANCE) {
        //API VERSION IS UNDER MAINTENANCE
        return res.json().then((data) => {
          if (data.error_code === 'maintenance_mode') {
            sessionStorage.setItem('maintenance_state', JSON.stringify(data.details));
            window.location.href = PAGE_URL.MAINTENANCE;
          }
          return { status: status, data };
        });
      } else if (
        status === 200 || status === 201 || status === 202 || status === 203 ||
        status === 205 || status === 206 || status === 207 || status === 208 || status === 226) {
        return res.json().then((data) => {
          return { status: status, data };
        });
      } else if (status === 204) {
        return { status: status };
      } else if (status === 401) {
        const data = await res.json();
        if (Object.values(FORCE_LOGOUT_REASON).includes(data.code)) {
          logout()(store.dispatch, store.getState, null);
        }
        return {status: 401, data};
      } else if (status === 403) {
        const data = await res.json();
        // NOTE: This is the generic code returned by Django REST Framwork when a PermissionDenied
        // is hit
        if (data.code === "permission_denied" && window.location.pathname !== PAGE_URL.SIGNUP) {
          window.location.href = PAGE_URL.DASHBOARD;
        } else {
          store.dispatch({ 
            type: ActionType.SET_FETCH_ERROR,
            error: {status_code: res.status, message: data.detail}
          });
        }
        return {status: 403, data};
      } else if (status >= 500) {
        return res.text().then((text) => {
          let data: { detail: E; code: string };
          try {
            // Try to parse as JSON first
            const parsed = JSON.parse(text);
            data = { detail: parsed as E, code: 'server_error' };
          } catch {
            data = { detail: text as E, code: 'server_error' };
          }
          cabCaptureException(Error(`${status} server error received for ${url}: ${JSON.stringify(data)}`));
          return { status, data };
        });
      } else {
        return res.json().then((data) => {
          return { status: status, data };
        });
      }
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .catch((res): Promise<FetchReturn<any, any>> => {
      if (res instanceof Error && !background) {
        const sanitizedContent = cloneDeep(content);
        if (sanitizedContent && sanitizedContent['headers'] && sanitizedContent['headers']['Authorization']) {
          sanitizedContent['headers']['Authorization'] = "SANITIZED";
        }
        
        console.log(JSON.stringify(res), url, JSON.stringify(sanitizedContent));
        cabCaptureException(res);
        // Returning valid structure here to avoid a redundant breaking exception in action creator
        if (['dev', 'stage'].includes(import.meta.env.VITE_DEPLOY_ENV || 'dev')) {
          // Only show this message on dev and stage.
          const message: GlobalMessage = {
            timeout: 5000,
            message: 'We\'re having trouble connecting - please try again in a few minutes.',
            autoDismiss: true,
            header: '',
            position: undefined,
            active: true,
            severity: "error",
          };

          sendMessage(message)(store.dispatch, store.getState, null);
        }
        return new Promise<FetchReturn>((resolve, reject): void => {
          resolve({ status: 999, data: { code: "network_error", detail: JSON.stringify(res) } });
        });
      } else if (!res) {
        // If a response is undefined, that's client problem and we don't want to
        // clutter Sentry or the console with it
        //console.log("Undefined response!");
        //Sentry.captureException(Error("Undefined response!"));
        return new Promise<FetchReturn<T>>((resolve, reject): void => {
          resolve({ status: 999, data: {code: "response_undefined", detail: "response undefined"} });
        });
      } else {
        // Report all other types of errors to sentry
        console.log('Server Error: ', res.statusText);
        cabCaptureException(Error(res.statusText));
        return new Promise<FetchReturn>((resolve, reject): void => {
          resolve({ status: res.status, data: {} });
        });
      }
    });
};

// Fetch a cookie by name
// eslint-disable-next-line max-len
// https://stackoverflow.com/questions/5639346/what-is-the-shortest-function-for-reading-a-cookie-by-name-in-javascript?rq=1
export const getCookieValue = (name: string): string => (
  document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')?.pop() || ''
);

export const parseFilesToB64Strings = async (files: File[]): Promise<string[]> => {
  const results = (await files?.map(async (file) => {
    const result = await new Promise<string | ArrayBuffer | null>((res) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = function () {
        res(reader.result);
      };
      reader.onerror = function (error) {
        throw Error("Parse file to string failed");
      };
    });
    return `${result}|${file.name}`;
  })) || [];

  return await Promise.all(results);
};

/**
 * Parses Django validation errors from an error object.
 *
 * Expects an object containing Django validation errors, typically with the following shape:
 * {
 *   data: {
 *     detail: {
 *       [field: string]: string[] | string
 *     }
 *   }
 * }
 *
 * Validates the shape of the error object and, if valid, parses the errors into an array of tuples,
 * where each tuple contains the field name and the corresponding error message(s) as a string.
 *
 * @template T - The type of the field names (usually string).
 * @param error - The error object to parse, expected to contain Django validation errors.
 * @returns An array of [field, error message] tuples if the shape is valid, or null otherwise.
 */
export const parseValidationErrors = <T extends string>(error: unknown): Array<[T, string]> | null => {
  // Validate that the error is of the expected shape
  if (typeof error === "object" && error 
    && "data" in error && error.data && typeof error.data === "object" 
    && "detail" in error.data && typeof error.data.detail === "object" && error.data.detail) {
    const fieldErrors = [] as Array<[T, string]>;
    for (const [key, value] of Object.entries(error.data.detail)) {
      if (typeof key === "string") {
        fieldErrors.push([key as T, Array.isArray(value) ? value.join(", ") : String(value)]);
      }
    }
    return fieldErrors;
  }
  return null;
};
